import { Button, Chip, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { Trans } from 'react-i18next'
import { DateTimePickerField, Icons, InformationModal } from 'utoniq-ui'

import {
  ArtistPreSaleLotteryApplicantsConnectionFragment,
  ConsoleLotteryManagementsQuery,
  Maybe,
  PreSaleLotteryApplicantFilterInputs,
  PreSaleLotteryResultNoticeStatus,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { dayjs } from '@/lib/dayjs'

import {
  LotteryApplicantEditModal,
  LotteryApplicantFilterCell,
  LotteryApplicantTableCell,
} from './components'

export * from './fragments'

export type State = {
  loading: boolean
  updatingResultNoticedAt: boolean
  hasNextPage: boolean
  artistId: string
  preSaleId: string
  preSale: Maybe<ConsoleLotteryManagementsQuery['artistPreSale']>
  lotteryApplicants: Maybe<ArtistPreSaleLotteryApplicantsConnectionFragment>
  isApplied: boolean
  updatingPreSaleLotteryApplicantStatus: boolean
  winDate?: string
  filters: PreSaleLotteryApplicantFilterInputs
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onFilters: (filters: PreSaleLotteryApplicantFilterInputs) => Promise<void>
  onSelectedWinner: (items: string[]) => Promise<void>
  onRegisterWinDate: (date: string) => Promise<void>
}

export const ConsoleLotteryManagementsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onFilters,
  onSelectedWinner,
  onRegisterWinDate,
}) => {
  const { t } = useTranslation('consoleLotteryManagements')
  const { routerNames, routerPages, generatePath, router } = useRouter<{
    preSaleId: string
  }>()
  const [openEditModal, setOpenEditModal] = useState<boolean>(false)

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleLotteryManagements} />
      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: generatePath(
            routerPages.ConsoleShokufesEventDetail,
            {
              eventId: state.preSale?.eventId ?? '#',
            }
          ),
          title: state.preSale?.title,
        }}
      >
        <HeadingTextCell preSaleId={state.preSaleId} />
        <StepCell
          updatingResultNoticedAt={state.updatingResultNoticedAt}
          resultNoticedAt={state.preSale?.lottery?.formattedResultNoticedAt}
          resultNoticeStatus={state.preSale?.lottery?.resultNoticeStatus}
          onRegisterWinDate={onRegisterWinDate}
        />

        <LotteryApplicantFilterCell
          maxChoice={state.preSale?.lottery?.maxChoice ?? 3}
          filters={state.filters}
          items={state.preSale?.items ?? []}
          onFilters={onFilters}
        />
        {/**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-14110
         */}
        {(state.preSale?.lottery?.resultNoticeStatus ===
          PreSaleLotteryResultNoticeStatus.None ||
          state.preSale?.lottery?.resultNoticeStatus ===
            PreSaleLotteryResultNoticeStatus.Ready) &&
          false && (
            <Stack alignItems="flex-end" pb={3}>
              <Button
                variant="text"
                sx={{ mr: -2 }}
                onClick={() => setOpenEditModal(true)}
              >
                {t('editWinner', '当選者を編集する')}
              </Button>
            </Stack>
          )}
        <LotteryApplicantTableCell
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          lotteryApplicants={state.lotteryApplicants}
        />
        <LotteryApplicantEditModal
          artistId={state.artistId}
          open={openEditModal}
          updatingPreSaleLotteryApplicantStatus={
            state.updatingPreSaleLotteryApplicantStatus
          }
          items={state.preSale?.items ?? []}
          filters={state.filters}
          maxChoice={state.preSale?.lottery?.maxChoice ?? 3}
          lotteryApplicants={state.lotteryApplicants}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          onClose={() => setOpenEditModal(false)}
          onFilters={onFilters}
          onSelectedWinner={onSelectedWinner}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type HeadingTextCellProps = {
  preSaleId: string
}
const HeadingTextCell: Cell<HeadingTextCellProps> = ({ preSaleId }) => {
  const { t } = useTranslation('consoleLotteryManagements')
  const { generatePath, routerPages } = useRouter()
  return (
    <CellFrame sx={{ py: 3, mt: 3, mb: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h3">
          {t('winnerManagement', '当選者管理')}
        </Typography>
        <Button
          variant="outlined"
          endIcon={<Icons.Next sx={{ color: 'primary.main' }} />}
          sx={{
            borderRadius: 1,
            color: 'primary.main',
            borderColor: 'primary.main',
          }}
          href={generatePath(routerPages.ConsoleLotteryCsvUpload, {
            preSaleId,
          })}
        >
          {t('bulkRegistrationWinners', '当選者の一括登録')}
        </Button>
      </Stack>
    </CellFrame>
  )
}

type StepCellProps = {
  resultNoticeStatus?: PreSaleLotteryResultNoticeStatus
  resultNoticedAt?: string | null
  updatingResultNoticedAt: boolean
  onRegisterWinDate: (date: string) => Promise<void>
}
const StepCell: Cell<StepCellProps> = ({
  resultNoticeStatus,
  resultNoticedAt,
  updatingResultNoticedAt,
  onRegisterWinDate,
}) => {
  const { t } = useTranslation('consoleLotteryManagements')
  const [openBulkRegistration, setOpenBulkRegistration] =
    useState<boolean>(false)
  const [dateTime, setDateTime] = useState<string | null>(
    resultNoticedAt ?? null
  )

  const step =
    resultNoticeStatus === PreSaleLotteryResultNoticeStatus.Ready
      ? 'Two'
      : 'One'

  return (
    <CellFrame
      sx={{
        py: 4,
        width: '100%',
        bgcolor: 'grey.50',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack
        flex={1}
        direction="row"
        alignItems="center"
        justifyContent={
          resultNoticeStatus !== PreSaleLotteryResultNoticeStatus.None &&
          resultNoticedAt
            ? 'space-between'
            : 'center'
        }
      >
        {resultNoticeStatus !== PreSaleLotteryResultNoticeStatus.None &&
        resultNoticedAt ? (
          <Stack direction="row" px={5} spacing={2} alignItems="center">
            {resultNoticeStatus !== PreSaleLotteryResultNoticeStatus.Done && (
              <Icons.Calendar sx={{ color: 'primary.main' }} />
            )}
            <Typography variant="subtitle2">
              <Trans
                i18nKey={
                  resultNoticedAt > DateTime.now()
                    ? 'winDateLabel'
                    : 'wonDateLabel'
                }
                t={t}
                values={{ date: resultNoticedAt }}
                components={{
                  linkTag: (
                    <Typography
                      component="span"
                      variant="subtitle2"
                      color="primary"
                    />
                  ),
                }}
              />
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" px={5} spacing={2} alignItems="center">
            <Chip
              color="primary"
              label={
                <Typography variant="subtitle2" color="white">
                  {t(`step${step}Key`)}
                </Typography>
              }
              sx={{ height: 16, borderRadius: '14px' }}
            />
            <Typography variant="subtitle1">{t(`step${step}Label`)}</Typography>
          </Stack>
        )}
        {resultNoticeStatus === PreSaleLotteryResultNoticeStatus.Ready && (
          <Button variant="text" onClick={() => setOpenBulkRegistration(true)}>
            {t('register', '登録')}
          </Button>
        )}
      </Stack>
      <InformationModal
        open={openBulkRegistration}
        onClose={() => setOpenBulkRegistration(false)}
        title={
          <Typography variant="h6" align="left">
            {t(
              'bulkRegistrationTitle',
              '当選確定を当選者にお知らせする日時を入力してください。'
            )}
          </Typography>
        }
        content={
          <Stack pb={7} pt={3} sx={{ textAlign: 'left' }}>
            <DateTimePickerField
              title={t('winningDateLabel', '当落発表日時')}
              titleProps={{ sx: { color: 'text.primary' } }}
              value={dateTime ? dayjs(dateTime) : null}
              onChange={date => setDateTime(date)}
            />
          </Stack>
        }
        cancelText={t('close')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            disabled={!dateTime}
            onClick={async () => {
              if (!dateTime) return
              onRegisterWinDate(dateTime).finally(() =>
                setOpenBulkRegistration(false)
              )
            }}
            loading={updatingResultNoticedAt}
          >
            {t('bulkRegister', '登録する')}
          </Button>
        }
      />
    </CellFrame>
  )
}
